/**
 * Layout — the application frame: sidebar, top bar, content well.
 *
 * router.tsx mounts this once as the parent route and pages render into its <Outlet>.
 * Pages do NOT render their own chrome; they render a <PageHeader> plus their content:
 *
 *   export default function Runs() {
 *     return (
 *       <>
 *         <PageHeader title="Runs" subtitle="Every dispatch, newest first"
 *                     actions={<Button variant="primary">New download</Button>} />
 *         <Card>…</Card>
 *       </>
 *     );
 *   }
 *
 * The top bar carries the environment chip and the live IST clock. Both are there for
 * one reason: revoking a grant or firing a dispatch against prod when you thought you
 * were in staging is not recoverable, and the clock keeps "is this window right?"
 * answerable without leaving the page.
 */

import vinferMark from '../assets/vinfer-mark.svg';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

import { signOut, useAuth } from '@/lib/auth';
import { nowIstLabel } from '@/lib/format';

import Button from './Button';
import Nav from './Nav';

// ---------------------------------------------------------------------------
// Theme
// ---------------------------------------------------------------------------

export type ThemePreference = 'light' | 'dark' | 'system';

const THEME_KEY = 'nrd.theme';

function readThemePreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(THEME_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    /* blocked storage: fall back to system */
  }
  return 'system';
}

function applyTheme(preference: ThemePreference): void {
  const dark =
    preference === 'dark' ||
    (preference === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
}

/**
 * Theme preference, persisted per browser. index.html applies the stored value before
 * first paint; this hook keeps it in sync afterwards and follows the OS while the
 * preference is 'system'.
 */
export function useTheme(): {
  preference: ThemePreference;
  setPreference: (next: ThemePreference) => void;
  toggle: () => void;
  resolved: 'light' | 'dark';
} {
  const [preference, setPreferenceState] = useState<ThemePreference>(readThemePreference);
  const [resolved, setResolved] = useState<'light' | 'dark'>(
    () => (document.documentElement.getAttribute('data-theme') as 'light' | 'dark') ?? 'light',
  );

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      window.localStorage.setItem(THEME_KEY, next);
    } catch {
      /* ignore */
    }
    applyTheme(next);
    setResolved((document.documentElement.getAttribute('data-theme') as 'light' | 'dark') ?? 'light');
  }, []);

  useEffect(() => {
    applyTheme(preference);
    if (preference !== 'system') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (): void => {
      applyTheme('system');
      setResolved(media.matches ? 'dark' : 'light');
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [preference]);

  const toggle = useCallback(() => {
    setPreference(resolved === 'dark' ? 'light' : 'dark');
  }, [resolved, setPreference]);

  return { preference, setPreference, toggle, resolved };
}

// ---------------------------------------------------------------------------
// Page header
// ---------------------------------------------------------------------------

export interface Crumb {
  label: string;
  /** Omit on the last crumb. */
  to?: string;
}

export interface PageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Right-aligned action buttons. Primary action last. */
  actions?: ReactNode;
  breadcrumbs?: Crumb[];
}

export function PageHeader({ title, subtitle, actions, breadcrumbs }: PageHeaderProps) {
  return (
    <header className="stack-tight">
      {breadcrumbs && breadcrumbs.length > 0 ? (
        <nav className="breadcrumbs" aria-label="Breadcrumb">
          {breadcrumbs.map((crumb, index) => (
            <span key={`${crumb.label}-${index}`} className="cluster-tight">
              {index > 0 ? (
                <span className="breadcrumb-sep" aria-hidden="true">
                  /
                </span>
              ) : null}
              {crumb.to ? (
                <Link to={crumb.to}>{crumb.label}</Link>
              ) : (
                <span className="breadcrumb-current" aria-current="page">
                  {crumb.label}
                </span>
              )}
            </span>
          ))}
        </nav>
      ) : null}

      <div className="page-head">
        <div className="grow">
          <h1 className="page-title">{title}</h1>
          {subtitle ? <div className="page-subtitle">{subtitle}</div> : null}
        </div>
        {actions ? <div className="page-actions">{actions}</div> : null}
      </div>
    </header>
  );
}

/** Bordered panel. `flush` drops body padding for a table that fills the card. */
export function Card({
  title,
  actions,
  children,
  footer,
  flush = false,
  className = '',
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  flush?: boolean;
  className?: string;
}) {
  return (
    <section className={`card ${className}`.trim()}>
      {title || actions ? (
        <div className="card-head">
          <div className="card-title">{title}</div>
          {actions ? <div className="cluster">{actions}</div> : null}
        </div>
      ) : null}
      <div className={flush ? 'card-body-flush' : 'card-body'}>{children}</div>
      {footer ? <div className="card-foot">{footer}</div> : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Shell
// ---------------------------------------------------------------------------

const APP_ENV = String(import.meta.env.VITE_APP_ENV ?? 'local').toLowerCase();

function EnvChip() {
  const tone = APP_ENV === 'prod' ? 'is-prod' : APP_ENV === 'staging' ? 'is-staging' : '';
  return (
    <span
      className={`env-chip ${tone}`.trim()}
      title={
        APP_ENV === 'prod'
          ? 'Production — revoking a link here breaks a real client download immediately'
          : `Environment: ${APP_ENV}`
      }
    >
      {APP_ENV}
    </span>
  );
}

/** Live IST clock. Ticks every 30s; the minute is the smallest unit anyone reads. */
function Clock() {
  const [label, setLabel] = useState(() => nowIstLabel());
  useEffect(() => {
    const timer = window.setInterval(() => setLabel(nowIstLabel()), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  return <span className="clock">{label}</span>;
}

function ThemeToggle() {
  const { resolved, toggle } = useTheme();
  return (
    <Button
      variant="ghost"
      size="sm"
      iconOnly
      onClick={toggle}
      title={`Switch to ${resolved === 'dark' ? 'light' : 'dark'} theme`}
      aria-label={`Switch to ${resolved === 'dark' ? 'light' : 'dark'} theme`}
      icon={
        resolved === 'dark' ? (
          <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
            <circle cx="8" cy="8" r="3.1" />
            <path d="M8 1.4v1.7M8 12.9v1.7M14.6 8h-1.7M3.1 8H1.4M12.7 3.3l-1.2 1.2M4.5 11.5l-1.2 1.2M12.7 12.7l-1.2-1.2M4.5 4.5 3.3 3.3" strokeLinecap="round" />
          </svg>
        ) : (
          <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
            <path d="M13.4 9.6A5.9 5.9 0 0 1 6.4 2.6a5.9 5.9 0 1 0 7 7Z" strokeLinejoin="round" />
          </svg>
        )
      }
    />
  );
}

function UserMenu() {
  const { identity } = useAuth();
  const [open, setOpen] = useState(false);

  const initials = useMemo(() => {
    const source = identity?.name || identity?.email || '?';
    const parts = source.split(/[\s@._-]+/).filter(Boolean);
    return ((parts[0]?.[0] ?? '?') + (parts[1]?.[0] ?? '')).toUpperCase();
  }, [identity]);

  if (!identity) return null;

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <span className="user-avatar" aria-hidden="true">
          {initials}
        </span>
        <span className="truncate" style={{ maxWidth: 160 }}>
          {identity.email || identity.username}
        </span>
      </button>

      {open ? (
        <>
          {/* Click-away layer. Sits under the menu, over everything else. */}
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 90,
              background: 'transparent',
              border: 0,
              cursor: 'default',
            }}
          />
          <div
            role="menu"
            className="card"
            style={{
              position: 'absolute',
              right: 0,
              top: 'calc(100% + 6px)',
              zIndex: 91,
              minWidth: 220,
              boxShadow: 'var(--shadow-2)',
            }}
          >
            <div className="card-body" style={{ padding: 'var(--s-3)' }}>
              <div className="stack-tight">
                <div className="strong truncate">{identity.name || identity.username || 'Operator'}</div>
                <div className="small dim truncate">{identity.email}</div>
                {identity.groups.length > 0 ? (
                  <div className="xs dim mono">{identity.groups.join(' · ')}</div>
                ) : null}
              </div>
            </div>
            <div className="card-foot" style={{ padding: 'var(--s-2)' }}>
              <Button variant="ghost" size="sm" block onClick={() => signOut()} role="menuitem">
                Sign out
              </Button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

export interface LayoutProps {
  /** Optional nav counts, keyed by route path. */
  navCounts?: Record<string, number | undefined>;
}

export function Layout({ navCounts }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  // Close the mobile drawer on navigation, otherwise it covers the page you asked for.
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  return (
    <div className={`app-shell ${sidebarOpen ? 'sidebar-open' : ''}`.trim()}>
      <aside className="app-sidebar">
        <div className="app-brand">
          <span className="app-brand-mark">
            <img src={vinferMark} alt="VInfer" width={16} height={16} />
          </span>
          <span className="stack-tight" style={{ gap: 0, minWidth: 0 }}>
            <span className="app-brand-name truncate">Report Dispatch</span>
            <span className="app-brand-sub">Neuron</span>
          </span>
        </div>

        <Nav counts={navCounts} onNavigate={() => setSidebarOpen(false)} />

        <div className="app-sidebar-foot">
          <div className="xs dim">
            Links expire 7 days after dispatch. Re-issuing mints a new token.
          </div>
        </div>
      </aside>

      {sidebarOpen ? (
        <button
          type="button"
          className="app-scrim"
          aria-label="Close navigation"
          onClick={() => setSidebarOpen(false)}
        />
      ) : null}

      <div className="app-main">
        <header className="app-topbar">
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            className="app-sidebar-toggle"
            onClick={() => setSidebarOpen((v) => !v)}
            aria-label="Toggle navigation"
            aria-expanded={sidebarOpen}
            icon={
              <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                <path d="M2.2 4h11.6M2.2 8h11.6M2.2 12h11.6" />
              </svg>
            }
          />

          <EnvChip />
          <div className="grow" />
          <Clock />
          <ThemeToggle />
          <UserMenu />
        </header>

        <main className="app-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default Layout;
